import { WebSocketsService } from './web-sockets.service';
import { ReplaySubject, Subscription, interval } from 'rxjs';
import { QueryParameter } from '../interfaces/queryString';
import { Lineup, LineupSchedule, LineupScheduleCycle } from '../interfaces/lineup';
import { SapiService } from './sapi.service';
import { Injectable } from '@angular/core';

@Injectable()
export class LineupService {

	maxLiveCount = 10;
	liveInfoThreshold = 7;
	initialLiveItemsDict: any = {};
	liveItemsDict: any = {};
	numUniqueLive: number;
	initialized = false;
	haveCycleGaps = false;
	readonly SECONDS_IN_BLOCK = 24 * 60 * 60;

	liveItemsSubject = new ReplaySubject<number>(1);
	hlsPushUrlSubject = new ReplaySubject<string>(1);
	channelStateSubject = new ReplaySubject<string>(1);
	channelUpdateSubject = new ReplaySubject<any>(1);
	currentLineupId: string;
	changePollInterval: Subscription;
	lineup: Lineup;

	private subscriptions = new Subscription();

	constructor(
		private sapiService: SapiService,
		private webSocketsService: WebSocketsService
	) {

	}

	initialize(lineup: Lineup) {
		if (this.currentLineupId !== lineup.id) {
			this.exit();
		}
		this.lineup = lineup;
		if (!this.initialized) {
			this.currentLineupId = lineup.id;
			this.initialized = true;

			this.subscriptions.add(this.webSocketsService.subscribeToChannel('Lineup').subscribe((websocketEvent: any) => {
				if (websocketEvent.payload && websocketEvent.id === lineup.id) {
					if (websocketEvent.payload && websocketEvent.ev === 'entityUpdate') {
						if (websocketEvent.payload.channelRestarting !== undefined) {
							lineup.channelRestarting = websocketEvent.payload.channelRestarting;
							this.channelUpdateSubject.next(websocketEvent);
						} else if (websocketEvent.payload.channelState && websocketEvent.payload.channelState !== lineup.channelState) {
							lineup.channelState = websocketEvent.payload.channelState;
							this.channelUpdateSubject.next(websocketEvent);
						}
					} else if (websocketEvent.payload && websocketEvent.ev === 'notification') {
						this.channelUpdateSubject.next(websocketEvent);
					}
				}
			}));
		}
		this.checkPollForChanges(lineup);
		this.checkLiveCount(lineup, true);
	}

	getAllGaps(lineup: LineupSchedule) {
		const gaps = [];
		this.haveCycleGaps = false;
		if (lineup && lineup.blocks && lineup.blocks.length) {
			lineup.blocks.forEach((block, i) => {
				if (block.cycles && block.cycles.length) {
					gaps[i] = this.getDayGaps(block.cycles);
				} else {
					gaps[i] = [];
					this.haveCycleGaps = true;
				}
			});
		}
		return gaps;
	}

	getDayGaps(cycles: LineupScheduleCycle[]) {
		const gaps = [];
		cycles.sort((a, b) => a.from > b.from ? 1 : -1);
		cycles.forEach((cycle, i) => {
			if (!cycle.items) {
				cycle.items = [];
			}
			if (i === 0) {
				if (cycle.from !== 0) {
					gaps.push({
						from: 0,
						to: cycle.from
					});
				}
			}
			const nextCycle = cycles[i + 1];
			if (nextCycle && cycle.to !== nextCycle.from) {
				gaps.push({
					from: cycle.to,
					to: nextCycle.from
				});
			} else if (!nextCycle && cycle.to !== this.SECONDS_IN_BLOCK) {
				gaps.push({
					from: cycle.to,
					to: this.SECONDS_IN_BLOCK
				});
			}
		});
		if (gaps.length) {
			this.haveCycleGaps = true;
		}
		return gaps;
	}

	checkPollForChanges(lineup: Lineup) {
		if (
			(!lineup.hlsPullUrl && lineup.hlsMethod === 'pull' || !lineup.channelState) && !this.changePollInterval) {
			this.changePollInterval = interval(15000).subscribe(() => {
				if (!this.lineup.hlsPullUrl || this.lineup.channelState === undefined) {
					this.sapiService.get('lineup', this.lineup.id).subscribe((sched: Lineup) => {
						if (sched && sched.hlsPullUrl && this.lineup.hlsPullUrl !== sched.hlsPullUrl) {
							this.lineup.hlsPullUrl = sched.hlsPullUrl;
							this.hlsPushUrlSubject.next(this.lineup.hlsPullUrl);
						}
						if (!this.lineup.channelState && sched.channelState) {
							this.lineup.channelState = sched.channelState;
							this.channelStateSubject.next(this.lineup.channelState);
						}
					});
				} else {
					this.changePollInterval.unsubscribe();
				}
			});
		}
	}

	exit() {
		if (this.changePollInterval) {
			this.changePollInterval.unsubscribe();
			this.changePollInterval = null;
		}
		this.subscriptions.unsubscribe();
		this.subscriptions = new Subscription();
		this.initialized = false;
	}

	checkLiveCount(lineupEntity: Lineup, fromInit = false) {
		const schedule = lineupEntity.schedule;
		if (!schedule || !schedule.blocks) {
			return;
		}
		this.liveItemsDict = {};
		const clipSearchParts = [];
		schedule.blocks.forEach(block => {
			block.cycles.forEach(cycle => {
				cycle.items.forEach(item => {
					if (item.entityType === 'MediaClip') {
						clipSearchParts.push(`id:"${item.entityId}"`);
					}
				});
			});
		});
		if (clipSearchParts.length) {
			const params: QueryParameter[] = [{
				name: 'q',
				values: [clipSearchParts.join(' OR ')]
			}];
			this.sapiService.query('commonclip', params, 0, 999).subscribe((response: any) => {
				if (response && response.items && response.items.length) {
					for (let i = 0, l = response.items.length; i < l; i++) {
						if (response.items[i] && response.items[i].sourcetype === 'live') {
							this.liveItemsDict[response.items[i].id] = response.items[i];
						}
					}
				}
				if (this.liveItemsDict) {
					this.numUniqueLive = (Object.keys(this.liveItemsDict)).length;
					this.liveItemsSubject.next(this.numUniqueLive);
				}
				if (!fromInit) {
					Object.keys(this.liveItemsDict).forEach((key) => {
						if (!this.initialLiveItemsDict[key]) {
							// live item was added
							lineupEntity.restartNeeded = true;
						}
					});
				} else {
					this.initialLiveItemsDict = this.liveItemsDict;
				}
			});
		}
	}

	start(id: string) {
		this.sapiService.get('lineup', id, 'requestChannelStart').subscribe((res: any) => {
		});
	}

	restart(id: string) {
		this.sapiService.get('lineup', id, 'requestChannelRestart').subscribe((res: any) => {
		});
	}

	stop(id: string) {
		this.sapiService.get('lineup', id, 'requestChannelStop').subscribe((res: any) => {
		});
	}
}
